import type { Accessor, Component } from 'solid-js'
import { For, Show } from 'solid-js'
import type { ChannelData } from '../../../features/engagement/domain/models/chat'
import Table, { type TableColumn, TableStatus } from './Table'
import TablePagination from './TablePagination'
import { Icon } from '@iconify-icon/solid'
import ChatRoomsTableRow from './ChatRoomsTableRow'

interface ChatRoomsTableProps {
  channels: ChannelData[]
  totalChannels: number
  page: Accessor<number>
  pageSize: number
  onPageChange: (page: number) => void
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  sortColumn: Accessor<string | null>
  sortDirection: Accessor<'asc' | 'desc'>
  isLoading?: boolean
}

const ChatRoomsTable: Component<ChatRoomsTableProps> = (props) => {
  const startIndex = () => (props.page() - 1) * props.pageSize

  const tableOrderBy = () => {
    const column = props.sortColumn()
    if (!column) {
      return undefined
    }
    return `${column}:${props.sortDirection()}`
  }

  const setTableOrderBy = (orderBy: string | undefined): void => {
    // The Table component cycles desc -> asc -> undefined. In this screen we keep desc -> asc -> desc.
    if (!orderBy) {
      const currentColumn = props.sortColumn()
      if (currentColumn) {
        props.onSort?.(currentColumn, 'desc')
      }
      return
    }

    const [column, direction] = orderBy.split(':')
    if (column && (direction === 'asc' || direction === 'desc')) {
      props.onSort?.(column, direction)
    }
  }

  const tableColumns: TableColumn[] = [
    {
      id: 'index',
      class: 'w-12 text-primary-600',
      header: <Icon icon="heroicons:chat-bubble-oval-left" class="text-[1.25rem]" />
    },
    {
      id: 'channels',
      class: 'text-primary-600'
    },
    {
      id: 'messages',
      class: 'text-right',
      sortable: true
    },
    {
      id: 'unique_chatters',
      class: 'text-right',
      sortable: true
    }
  ]

  const status = () => {
    if (props.isLoading && props.channels.length === 0) {
      return TableStatus.Init
    }
    if (props.isLoading) {
      return TableStatus.Loading
    }
    if (props.channels.length === 0) {
      return TableStatus.NoData
    }
    return TableStatus.Loaded
  }

  return (
    <div class="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div class="border-t border-gray-200">
        <div class="overflow-x-auto">
          <Table
            status={status}
            columns={tableColumns}
            orderBy={tableOrderBy}
            setOrderBy={setTableOrderBy}
            tableId="chat"
          >
            <For each={props.channels}>
              {(channel, index) => <ChatRoomsTableRow channel={channel} rowNumber={startIndex() + index() + 1} />}
            </For>
          </Table>
        </div>
        <Show when={props.totalChannels > props.pageSize}>
          <TablePagination
            totalCount={props.totalChannels}
            pageSize={props.pageSize}
            page={props.page}
            setPage={props.onPageChange}
          />
        </Show>
      </div>
    </div>
  )
}

export default ChatRoomsTable
